<template>
    <div class="onboarding-container">
        <div class="col-lg-6 onboarding-content">
            <div class="logo">
                <img src="/img/ashore-logo.svg" alt="Ashore" width="170">
            </div>
            <Screen1
                v-if="currentStep === 1"
                :currentStep="currentStep"
                :handleNextStep="handleNextStep"
                :featuresIncludes="featuresIncludes"
                :featuresCanUnlock="featuresCanUnlock"
                />
            <Screen2 v-if="currentStep === 2" :currentStep="currentStep" :handleNextStep="handleNextStep"
                :handlePrevStep="handlePrevStep" />
            <Screen3 v-if="currentStep === 3" :currentStep.sync="currentStep" :handleNextStep="handleNextStep"
                :handlePrevStep="handlePrevStep" :checklist-items="checklistItems" @show-modal="showModal" />
            <newUserWelcomeModal name="NewUserWelcomeModal" @modal-closed="onModalClosed" />
            <Screen4 v-if="currentStep === 4" :featuresCanUnlock="featuresCanUnlock" :currentStep="currentStep" />
        </div>
        <div v-if="currentStep < 4" class="col-lg-6 d-none d-lg-flex p-0 overflow-hidden position-relative bg-black">
            <OnboardingSlide />
        </div>
        <div v-if="currentStep === 4" class="col-lg-6 d-none d-lg-flex flex-column align-items-center justify-content-center px-5 py-5 bg-white video-container">
            <!-- Wistia Video Embed -->
            <div class="video-wrapper">
                <div class="wistia_responsive_wrapper">
                    <div class="wistia_embed wistia_async_pvry2omp6m seo=true videoFoam=true">
                        <div class="wistia_swatch">
                            <img src="https://fast.wistia.com/embed/medias/pvry2omp6m/swatch"
                                alt=""
                                aria-hidden="true" onload="this.parentNode.style.opacity=1;" />
                        </div>
                    </div>
                </div>
            </div>
            <!-- Quote Section -->
            <div class="quote-section">
                <img src="/img/quotes-green.svg" alt="Quote" class="quote-icon" />
                <div class="text-start">
                    <div class="quote-text">
                        I can't imagine not having Ashore now!
                    </div>
                    <div class="quote-author">
                        JESSE, ALPINE APPAREL COMPANY
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Screen1 from '../components/onboarding/screen1.vue';
import Screen2 from '../components/onboarding/screen2.vue';
import Screen3 from '../components/onboarding/screen3.vue';
import newUserWelcomeModal from '../components/modals/modal-new-user-welcome.vue';
import Screen4 from '../components/onboarding/screen4.vue';
import OnboardingSlide from '../components/onboarding/onboardingSlide.vue';

const DEFAULT_CHECKLIST_ITEMS = [
    { name: "I have checked for spelling and errors" },
    { name: "I assume liability for any mistakes" },
    { name: "I understand that upon my approval, this is now going to production" }
];

const FEATURES_INCLUDES = [
    'PDF, image and web proofs',
    'Real-time creative collaboration',
    'Proof approval checklists / e-Signature',
];

const FEATURES_CAN_UNLOCK = [
    'Full workflow automation',
    'Customizable email templates',
    'Audio and video proofs',
    'More files and unlimited storage limits',
    'Full review screen white-labeling',
    'Unlimited custom fields',
    'Zapier integration',
    'Adobe plugins',
    'API access',
];

export default {
    name: "OnboardingScreen",
    components: {
        Screen1,
        Screen2,
        Screen3,
        newUserWelcomeModal,
        Screen4,
        OnboardingSlide
    },
    data() {
        return {
            currentStep: 1,
            checklistItems: [],
            defaultChecklistItems: DEFAULT_CHECKLIST_ITEMS,
            featuresIncludes: FEATURES_INCLUDES,
            featuresCanUnlock: FEATURES_CAN_UNLOCK,
        };
    },
    created() {
        this.initializeOnboarding();
    },
    async mounted() {
        await this.loadWistiaScripts();
        await this.loadChecklist();
    },
    methods: {
        initializeOnboarding() {
            this.currentStep = this.$A.Auth.onboardingStep ?? 1;
        },
        async loadWistiaScripts() {
            const scripts = [
                {
                    src: 'https://fast.wistia.com/embed/medias/pvry2omp6m.jsonp',
                    async: true
                },
                {
                    src: 'https://fast.wistia.com/assets/external/E-v1.js',
                    async: true
                }
            ];

            scripts.forEach(script => {
                const scriptElement = document.createElement('script');
                Object.assign(scriptElement, script);
                document.head.appendChild(scriptElement);
            });
        },
        async handleNextStep() {
            if (this.currentStep >= 4) {
                return this.currentStep = 4;
            }
            this.currentStep++;
            const onboardingStep = _.get(this, '$A.Auth.onboardingStep', 1);
            if(onboardingStep >= this.currentStep) return;
            try {
                const currentUser = _.get(this, '$A.Auth', null);
                if (!currentUser) return;
                const response = await this.$A.UserService.UpdateUser(currentUser.id, { onboardingStep: this.currentStep, email: currentUser.email }, false)
                response && this.$A.UpdateUserObjectAndBustCache(response);
            } catch (error) {
                this.$A.AlertUser("Failed to update onboarding step. Please try again.", "error");
                this.currentStep--;
            }
        },
        async handlePrevStep() {
            if (this.currentStep <= 1) {
                return this.currentStep = 1;
            }
            this.currentStep--;
        },
        showModal() {
            this.$bvModal.show("NewUserWelcomeModal");
        },
        async loadChecklist() {
            try {
                const checklists = await this.$A.ProofChecklistService.GetAllProofChecklistsItems();
                if (Array.isArray(checklists) && checklists.length > 0) {
                    const [firstChecklist] = checklists;
                    this.checklistItems = {...firstChecklist,items: [...firstChecklist.items, ...this.defaultChecklistItems],};
                } else {
                    this.$A.AlertUser("No checklists found.", "warning");
                }
            } catch (error) {
                this.$A.AlertUser("There was an issue with the API. Please try logging in again.", "warning");
            }
        },
    },
};
</script>

<style scoped>
.onboarding-container {
    height: 100vh;
    display: flex;
    padding: 0;
    justify-content: space-between;
    overflow: hidden;
}

.text-green-800 {
    color: #264C29;
}

.onboarding-content {
    overflow: auto;
    padding: 30px 30px;
}

.video-container {
    min-height: 100vh;
    border: 1px solid #EBEBED;
}

.video-wrapper {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 56.25%;
    margin-bottom: 0;
}

.wistia_responsive_wrapper {
    height: 100%;
    left: 0;
    position: absolute;
    width: 100%;
}

.wistia_embed {
    height: 100%;
    position: relative;
    width: 100%;
}

.wistia_swatch {
    height: 100%;
    left: 0;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    transition: opacity 200ms;
    width: 100%;
}

.wistia_swatch img {
    filter: blur(5px);
    height: 100%;
    object-fit: contain;
    width: 100%;
}

.quote-section {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 0;
    padding-top: 12px;
    width: 100%;
}

.quote-icon {
    width: auto;
    height: auto;
}

.quote-text {
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
    font-size: 36px;
    line-height: 40px;
    letter-spacing: 0%;
    color: #264C29;
}

.quote-author {
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 12px;
    margin-top: 8px;
    color: #006F41;
    text-transform: uppercase;
}

@media (min-width: 768px) {
    .onboarding-content {
        padding: 30px 50px;
    }
}

@media (min-width: 1440px) {
    .onboarding-content {
        padding: 35px 96px;
    }

    .logo {
        margin-top: 1rem;
    }
}
</style>
